import React, {useEffect, useState, useRef} from 'react';
import {store} from '../../app/store';
import styles from './FeaturedVideo.module.scss';
import {useSelector} from 'react-redux';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import {addSectionItem, deleteMediakitSectionItem, MediakitSectionItem, sortMediakitSectionItems, updateSectionItem} from './../../features/stores/mediakitUser.slice';
import {convertTime, extractVideoUrl, reorder} from '../../features/util';
import FeaturedVideoItem from './FeaturedVideoItem';
import axios from 'axios';
import {useTranslation} from "react-i18next";
import noVideo from './../../assets/images/icons/noVideo.png';
import useWindowWidth from '../../hooks/useWindowWidth';
import {selectMediakitUser} from '../../features/stores/mediakitUser.slice';
import {setDataChange} from '../../features/stores/changeData.slice';
import AlertDialog from '../UnsavedChanges/AlertDialog';
import PublishButton from '../PublishButton';
import nonHookRequest from '../../API/nonHookRequest';
import {TailSpin} from 'react-loader-spinner';
import {toast} from 'react-toastify';
import TitleWrapper from '../TitleWrapper';

const socialPlatform: any = {
  instagram: 'Instagram',
  youtube: 'Youtube',
  tiktok: 'TikTok',
};

const FeaturedVideo = () => {
  const {t} = useTranslation();
  const mediakitUser = useSelector(selectMediakitUser);
  const mediakitUserSections = mediakitUser.mediakitSection;
  const featuredVideosSection = mediakitUserSections.find(
      (s: any) => s.type === 'video',
  );
  const mobile = useWindowWidth();
  const featuredVideos = featuredVideosSection?.mediakitSectionLinks?.filter((e: any) => e.isActive != false) || [];
  const featuredVideosI = featuredVideosSection?.mediakitSectionLinks?.filter((e: any) => e.isActive != false && e.platform == 'instagram') || [];
  const featuredVideosT = featuredVideosSection?.mediakitSectionLinks?.filter((e: any) => e.isActive != false && e.platform == 'tiktok') || [];
  const featuredVideosY = featuredVideosSection?.mediakitSectionLinks?.filter((e: any) => e.isActive != false && e.platform == 'youtube') || [];
  const [newVideoURL, setNewVideoURL] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalButtonText, setModalButtonText] = useState('');
  const [activeTab, setActiveTab] = useState('instagram');
  const [dataFetch, setDataFetch] = useState(false);

  const handleAppendVideoToList = (obj: MediakitSectionItem) => {
    const appendItem = () => new Promise<void>((resolve, reject) => {
      featuredVideos?.forEach((element: MediakitSectionItem, index: number) => {
        store.dispatch(updateSectionItem({id: element?.id, data: {...element, orderNumber: index+1}}));
      });
      resolve();
    });
    appendItem().then(() => {
      store.dispatch(addSectionItem(obj));
    }).then(() => {
      store.dispatch(sortMediakitSectionItems());
    });
    store.dispatch(setDataChange(true));
    setNewVideoURL('');
    setActiveTab(obj?.platform);
  };

  const handleAddVideo = async () => {
    let tempUrl = newVideoURL;
    if (!newVideoURL.toLowerCase()?.startsWith('https://')) {
      setNewVideoURL(`https://${newVideoURL}`);
      tempUrl = `https://${newVideoURL}`;
    }
    const validYoutubeUrls = ['youtube.com', 'https://www.youtube.com/watch?v=', 'https://youtu.be', 'www.youtube.com/watch?v=', 'https://youtube.com/watch?v='];
    const validInstagramUrls = ['instagram.com', 'https://www.instagram.com', 'www.instagram.com', 'https://instagram.com'];
    const validTikTokUrls = ['tiktok.com', 'https://www.tiktok.com', 'www.tiktok.com', 'https://tiktok.com', 'vt.tiktok.com', 'https://www.vt.tiktok.com', 'www.vt.tiktok.com', 'https://vt.tiktok.com'];
    const linkStartsWith = (url:string)=>tempUrl?.startsWith(url);
    const youtubeLinkFound = validYoutubeUrls.some(linkStartsWith);
    const instagramLinkFound = validInstagramUrls.some(linkStartsWith);
    const tiktokLinkFound = validTikTokUrls.some(linkStartsWith);

    if (!tempUrl || (!youtubeLinkFound && !instagramLinkFound && !tiktokLinkFound)) {
      setShowModal(true);
      setModalText(t('invalid_url_error'));
      setModalButtonText(t('featured_video_add_btn_text'));
      return;
    }

    if (youtubeLinkFound) {
      handleAddYoutubeVideo();
    } else if (instagramLinkFound || tiktokLinkFound) {
      handleAddPostCIQ(tempUrl);
    } else {
      toast(<span style={{color: 'black'}}>{'Something went wrong!'}</span>, {autoClose: 3000});
    }
  };

  const handleAddYoutubeVideo = async () => {
    let title = '';
    let duration = '';
    let channelIconUrl = '';
    setDataFetch(true);
    const id = extractVideoUrl(newVideoURL);
    const youtubeUrl = 'https://youtube.googleapis.com/youtube/v3/videos?part=snippet&part=contentDetails&part=topicDetails&part=statistics&id=' + id + '&key=' + process.env.REACT_APP_YOUTUBE_API_KEY;
    const videoDetails = await axios.get(youtubeUrl);

    if (videoDetails.data.items.length) {
      const item = videoDetails.data.items[0];
      duration = convertTime(item.contentDetails.duration);
      title = item.snippet.title;
      const youtubeChannelDetailsUrl = 'https://youtube.googleapis.com/youtube/v3/channels?part=snippet' + '&id=' + item.snippet.channelId +'&key=' + process.env.REACT_APP_YOUTUBE_API_KEY;
      // get channel profile icon
      const channelDetails = await axios.get(youtubeChannelDetailsUrl);
      if (channelDetails.data) {
        channelIconUrl = channelDetails.data.items[0]?.snippet.thumbnails.high.url || '';
      }

      const obj: MediakitSectionItem = {
        id: Date.now().toString(),
        url: newVideoURL,
        title: title,
        duration,
        channelProfileIcon: channelIconUrl,
        videoThumbnail: item.snippet.thumbnails?.maxres?.url || item.snippet.thumbnails?.high?.url,
        customUploadedImage: null,
        platform: 'youtube',
        mediakitSectionId: featuredVideosSection?.id as string,
        orderNumber: 0,
        isVisible: true,
        contentType: 'video',
        isMonetize: false,
        description: title,
        metadata: {
          comments: item?.statistics?.commentCount || 0,
          likes: item?.statistics?.likeCount || 0,
          views: item?.statistics?.viewCount || 0,
        },
      };
      handleAppendVideoToList(obj);
      setDataFetch(false);
    } else {
      setShowModal(true);
      setModalText(t('video_add_error'));
      setModalButtonText(t('featured_video_add_btn_text'));
      setDataFetch(false);
      return;
    }
  };

  const handleAddPostCIQ = async (tempUrl: string) => {
    try {
      setDataFetch(true);
      const platformResponse = await nonHookRequest({method: 'GET', url: '/api/v1/social-link/video/details', params: {postUrl: tempUrl}, isShortUrl: true});
      const obj: MediakitSectionItem = {
        id: Date.now().toString(),
        url: tempUrl,
        title: platformResponse[platformResponse?.type][platformResponse[platformResponse?.type]?.network == 'instagram' ? 'caption' : 'info'] || platformResponse[platformResponse?.type]['userName'],
        duration: null,
        channelProfileIcon: undefined,
        videoThumbnail: platformResponse[platformResponse?.type]?.savedThumbnail,
        customUploadedImage: null,
        platform: platformResponse[platformResponse?.type]?.network,
        mediakitSectionId: featuredVideosSection?.id as string,
        orderNumber: 0,
        isVisible: true,
        contentType: platformResponse[platformResponse?.type]?.type,
        isMonetize: false,
        description: platformResponse[platformResponse?.type][platformResponse[platformResponse?.type]?.network == 'instagram' ? 'caption' : 'info'] || platformResponse[platformResponse?.type]['userName'],
        metadata: {
          comments: platformResponse[platformResponse?.type]?.comments || 0,
          likes: platformResponse[platformResponse?.type]?.likes || 0,
          views: platformResponse[platformResponse?.type]?.views || platformResponse[platformResponse?.type]?.engagement || 0,
          shares: platformResponse[platformResponse?.type]?.shares || 0,
        },
      };
      handleAppendVideoToList(obj);
      setDataFetch(false);
    } catch (error) {
      setShowModal(true);
      setModalText(t('fetch_error'));
      setModalButtonText(t('close'));
      setDataFetch(false);
    }
  };

  const scrollableDivRef = useRef() as any;

  const onDragEnd = (result: any)=>{
    if (!result.destination) {
      return;
    }
    const itemsTemp = reorder(
        featuredVideos.filter((e) => e.platform == activeTab),
        result.source.index,
        result.destination.index,
    ) as any;

    const final = itemsTemp.map((el: any, index: number) => {
      return {...el, orderNumber: index};
    });
    final.forEach((element: MediakitSectionItem, index: number) => {
      store.dispatch(updateSectionItem({id: element?.id, data: {...element, orderNumber: index}}));
    });
    store.dispatch(sortMediakitSectionItems());
    store.dispatch(setDataChange(true));
  };

  const handleDelete = async (id: string) => {
    // store.dispatch(setDataChange(true));
    // store.dispatch(deleteMediakitSectionItem({sectionItemId: id}));
    store.dispatch(updateSectionItem({id, data: {isActive: false}}));
    store.dispatch(setDataChange(true));
  };

  return (
    <div className={styles.container}>
      <AlertDialog setIsOpen={setShowModal} open={showModal} contentHeadline={modalText} buttonText={modalButtonText}/>
      <TitleWrapper title={t('past_campaigns')} subtitle={t('highlight_your_previous_work')} />
      <div className={styles.new_link_wrapper}>
        <input
          className={styles.new_link_input}
          value={newVideoURL}
          onChange={(e) => {
            setNewVideoURL(e.target.value);
          }}
          placeholder={t('yt_video_url')}
        />
        <div className={`${styles.save_button} ${dataFetch ? styles.disabled : ''}`} onClick={() => handleAddVideo()}>
            {dataFetch ? <TailSpin color="#FFF" height={20} width={20} /> : t('add_video')}
        </div>
      </div>

      {/* {(featuredVideos?.length !== 0) &&
      <div className={styles.addedContainer}>
        <div className={styles.added}>{t('added_videos')}</div>

        <NaviButtons onLeftClick={() => scrollableDivRef.current.scrollLeft -= 259}
          onRightClick={() => {
            scrollableDivRef.current.scrollLeft += 259;
          }}/>

      </div>
      } */}
      <div className={styles.tab_pill_container}>
        {['instagram', 'tiktok', 'youtube'].map((tab) => (
          <div key={tab} className={`${styles.tab_item} ${activeTab === tab ? styles.tab_active : ''}`} onClick={() => setActiveTab(tab)}>{socialPlatform[tab]}</div>
        ))}
      </div>
      {featuredVideos.filter((e) => e.platform == activeTab)?.length ? <div className={styles.scrollable} ref={scrollableDivRef} key={featuredVideos?.length}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable direction='vertical' droppableId="droppable">
            {(provided, snapshot) => (
              <div
                style={{display: 'flex', flexWrap: 'wrap', gap: '40px'}}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {featuredVideos.filter((e) => e.platform == activeTab)?.map((item: MediakitSectionItem, index: number) => {
                  return (
                    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          // className={styles.scrollableItem}
                        >
                          <FeaturedVideoItem
                            link={item}
                            onDeleteClick={handleDelete}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div> : <div className={styles.no_links}>
        <img src={noVideo} />
        <div className={styles.no_links_text}>
        <div className={styles.no_links_title}>{t('no_video_to_display')}</div>
        <div className={styles.subtitle_no_link}>{t('click_the_button_above_to_add_one')}</div>
        </div>
      </div>}
      <div className={styles.publish_conatiner}>
        <PublishButton />
      </div>
    </div>
  );
};

export default FeaturedVideo;


// const validate = () => {
//   let flagEmpty: boolean = false;
//   let isUrlOk: boolean = true;
//   if (newVideoURL === '') {
//     flagEmpty = true;
//   }
//   if (
//     !newVideoURL?.startsWith('https://www.youtube.com/watch?v=') &&
//       !newVideoURL?.startsWith('https://youtu.be')
//   ) {
//     isUrlOk = false;
//   }
//   // if (!check && (flagEmpty || !isUrlOk)) {
//   //   return;
//   // }
//   // if (flagEmpty || !isUrlOk) {
//   //   store.dispatch(setSectionStatus({
//   //     section: SECTION_NAMES.VIDEOS,
//   //     valid: false,
//   //     saveStatus: false,
//   //   }));
//   // } else {
//   //   store.dispatch(setSectionStatus({
//   //     section: SECTION_NAMES.VIDEOS,
//   //     valid: true,
//   //     saveStatus: false,
//   //   }));
//   // }
//   return {flagEmpty, isUrlOk};
// };

// const validateWithModal = () => {
//   const {flagEmpty, isUrlOk} = validate();
//   if (flagEmpty) {
//     setIsModalOpened(true);
//     setModalContextText('Please link directly to a YouTube video');
//     setModalButtonText('Add correct URL');
//     return false;
//   }
//   if (!isUrlOk) {
//     setIsModalOpened(true);
//     setModalContextText('Please link directly to a YouTube video');
//     setModalButtonText('Add correct URL');
//     return false;
//   }
//   return true;
// };
